import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { Spin } from 'antd'
import { useAuthStore } from '@/stores/authStore'
import { useConfiguracaoStore } from '@/stores/configuracaoStore'

export function AuthGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, loading } = useAuthStore()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

export function ContractGuard({ children }: { children: ReactNode }) {
  const parceiro = useAuthStore((s) => s.parceiro)
  const isAdministrador = useAuthStore((s) => s.isAdministrador)

  if (isAdministrador) {
    if (!parceiro) return <Navigate to="/selecionar-parceiro" replace />
    return <>{children}</>
  }

  // Usuário comum vinculado a vários parceiros entra sem nenhum escolhido (spec 017). Mandá-lo ao
  // contrato aqui seria pedir aceite de um parceiro que ele ainda não escolheu.
  if (!parceiro) return <Navigate to="/selecionar-parceiro" replace />

  if (!parceiro.cpfAceiteContrato) {
    return <Navigate to="/contrato" replace />
  }

  return <>{children}</>
}

// Preços is off-limits to a captador (parceiroPaiId set): it configures no one. Only an admin of
// New Driver or of a top-level parceiro may reach the page.
export function PrecosGuard({ children }: { children: ReactNode }) {
  const parceiro = useAuthStore((s) => s.parceiro)

  if (parceiro?.role !== 'admin' || parceiro?.parceiroPaiId != null) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

export function MatriculaGuard({ children }: { children: ReactNode }) {
  const carregado = useConfiguracaoStore((s) => s.carregado)

  if (carregado) return <Navigate to="/precos" replace />

  return <>{children}</>
}

export function PublicOnlyGuard({ children }: { children: ReactNode }) {
  const { isAuthenticated, isAdministrador, parceiro, loading } = useAuthStore()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" />
      </div>
    )
  }

  if (isAuthenticated) {
    if (isAdministrador && !parceiro) return <Navigate to="/selecionar-parceiro" replace />
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}
